import { useCallback, useState } from 'react'
import {
  UserDropdownInfiniteScrollProps,
  UserDropdownProps,
  userIdOption,
  UserOption,
} from 'src/components/UserDropdown/UserDropdown'
import {
  useQueryOwnUser,
  useQueryUser,
  useQueryUsersSearch,
} from 'src/queries/users.queries'
import { GetUsersSearchRequest } from 'src/requests/getUsers.request'

interface UseUserSearchProps {
  enabled?: boolean
  filters?: Partial<GetUsersSearchRequest>
  selectedValues?: Set<string>
  onSelectionChange?: (value: string) => void
  placeholder?: string
  isMulti?: boolean
}

export const useUserSearch = ({
  enabled = true,
  filters = {},
  selectedValues = new Set(),
  onSelectionChange,
  placeholder,
  isMulti = false,
}: UseUserSearchProps): UserDropdownProps => {
  const [searchQuery, setSearchQuery] = useState<string | undefined>()
  const { data: user } = useQueryOwnUser({})

  const {
    data: usersData,
    fetchNextPage: fetchMoreUsers,
    isFetching: usersLoading,
    complete: usersComplete,
  } = useQueryUsersSearch({
    filters: {
      q: searchQuery,
      inOrganization: true,
      count: 10,
      ...filters,
    },
    enabled,
  })

  const userOptions: UserOption[] = [
    // Include current user if available
    user ? userIdOption(user) : undefined,
    // Include other users from search results
    ...(usersData?.users?.map((user) => userIdOption(user)) ?? []),
  ].filter((option): option is UserOption => !!option)

  // Resolve a URL-preselected user by id so the dropdown renders a labeled chip
  // instead of a raw id when that user isn't on the first search page. Deep
  // links seed selectedValues with a single user; users added via the dropdown
  // already come labeled from the fetched options above.
  const preselectedUserId = Array.from(selectedValues)[0]
  const { data: preselectedUser } = useQueryUser({
    userId: preselectedUserId,
    enabled: enabled && preselectedUserId !== undefined,
  })
  const selectedOptions: UserOption[] | undefined = preselectedUser
    ? [userIdOption(preselectedUser)]
    : undefined

  const infiniteScrollProps: UserDropdownInfiniteScrollProps = {
    fetch: (filterInput, page) => {
      if (page === 0) setSearchQuery(filterInput)
      fetchMoreUsers()
    },
    page: usersData?.page,
    loading: usersLoading,
    complete: usersComplete,
  }

  const handleOptionSelect = useCallback(
    (value: string) => {
      onSelectionChange?.(value)
    },
    [onSelectionChange],
  )

  return {
    optionGroups: [{ options: userOptions }],
    selectedValues,
    selectedOptions,
    chooseOption: handleOptionSelect,
    placeholder,
    isMulti,
    isCreatable: false,
    infiniteScrollProps,
  }
}
